import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Checkbox,
    Chip,
    Divider,
    Alert,
    LinearProgress,
    Card,
    CardContent,
    useTheme,
    alpha,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SpeedIcon from '@mui/icons-material/Speed';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TimerIcon from '@mui/icons-material/Timer';
import { Helmet } from 'react-helmet-async';
import api from '../services/api';

export default function ReadingBlueprint() {
    const { token } = useParams();
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [blueprint, setBlueprint] = useState(null);
    const [dayProgress, setDayProgress] = useState([]);

    useEffect(() => {
        let mounted = true;

        async function fetchBlueprint() {
            try {
                const res = await api.get(`/blueprint/${token}`);
                if (mounted) {
                    setBlueprint(res.data);
                    setDayProgress(res.data.sevenDayPlan?.map(d => d.completed) || []);
                    setLoading(false);
                }
            } catch (err) {
                if (mounted) {
                    setError(err.response?.data?.error || 'Failed to load blueprint.');
                    setLoading(false);
                }
            }
        }

        if (token) fetchBlueprint();

        return () => { mounted = false; };
    }, [token]);

    const handleToggleDay = async (day) => {
        const newProgress = [...dayProgress];
        newProgress[day - 1] = !newProgress[day - 1];
        setDayProgress(newProgress);

        try {
            await api.post(`/blueprint/${token}/mark-day`, {
                day,
                completed: newProgress[day - 1],
            });
        } catch (err) {
            // Revert on error
            newProgress[day - 1] = !newProgress[day - 1];
            setDayProgress(newProgress);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Your blueprint link may have expired. Blueprints are available for 14 days after your assessment.
                </Typography>
                <Button component={Link} to="/speed-test" variant="contained" color="primary">
                    Take the Speed Test Again
                </Button>
            </Container>
        );
    }

    if (!blueprint) return null;

    const b = blueprint.baseline;
    const fp = blueprint.frictionProfile;
    const guided = blueprint.guided;
    const completedDays = dayProgress.filter(Boolean).length;

    return (
        <>
            <Helmet>
                <title>Your Reading Blueprint | ReaderFLEX</title>
                <meta name="robots" content="noindex" />
            </Helmet>

            <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
                {/* ── HEADER ── */}
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 800, color: theme.palette.primary.main, mb: 1 }}>
                        ReaderFLEX
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Speed Reading Platform
                    </Typography>
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
                        Your Personal Reading Blueprint
                    </Typography>
                    {blueprint.firstName && (
                        <Typography variant="h6" color="text.secondary">
                            Prepared for {blueprint.firstName}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        Generated {new Date(blueprint.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </Typography>
                </Box>

                {/* ── PDF DOWNLOAD + TRIAL CTA ── */}
                <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<DownloadIcon />}
                        href={`${api.defaults.baseURL}/blueprint/${token}/pdf`}
                        target="_blank"
                    >
                        Download PDF
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<RocketLaunchIcon />}
                        component={Link}
                        to="/subscription?planType=membership&billingCycle=trial&from=blueprint"
                        sx={{
                            background: 'linear-gradient(135deg, #00BFFF 0%, #0099CC 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #00A5E0 0%, #0088BB 100%)' },
                        }}
                    >
                        Start $1 Trial
                    </Button>
                </Box>

                {/* ── SECTION 2: READING SNAPSHOT ── */}
                <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <SpeedIcon color="primary" />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Your Reading Snapshot</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Reading Speed</Typography>
                            <Typography variant="h3" sx={{ color: '#00BFFF', fontWeight: 700 }}>{b.wpm}</Typography>
                            <Typography variant="caption" color="text.secondary">WPM</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Comprehension</Typography>
                            <Typography variant="h3" sx={{ color: '#8DC63F', fontWeight: 700 }}>{b.comprehension}%</Typography>
                            <Typography variant="caption" color="text.secondary">{b.correct}/{b.total} correct</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Effective WPM</Typography>
                            <Typography variant="h3" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>{b.effectiveWpm}</Typography>
                            <Typography variant="caption" color="text.secondary">speed × comprehension</Typography>
                        </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                        Effective WPM combines your pace with your comprehension score. It helps prevent speed alone from being mistaken for meaningful reading progress.
                    </Typography>
                </Paper>

                {/* ── SECTION 3: FRICTION PROFILE ── */}
                <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <PsychologyIcon color="primary" />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Your Friction Profile</Typography>
                    </Box>
                    <Chip label={fp.name} color="primary" sx={{ mb: 2, fontSize: 14 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 3 }}>
                        {fp.description}
                    </Typography>
                    <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 2, p: 3, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Your first step:</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                            {fp.firstStep}
                        </Typography>
                    </Box>
                    <Box sx={{ bgcolor: alpha('#ff9800', 0.04), borderRadius: 2, p: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>What not to assume:</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                            {fp.whatNotToAssume}
                        </Typography>
                    </Box>
                </Paper>

                {/* ── SECTIONS 4-5: STRENGTH & OPPORTUNITY ── */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Paper sx={{ p: 3, borderRadius: 3, flex: 1, minWidth: 250 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TrendingUpIcon sx={{ color: '#8DC63F' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Primary Strength</Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#8DC63F' }}>
                            {blueprint.strength}
                        </Typography>
                    </Paper>
                    <Paper sx={{ p: 3, borderRadius: 3, flex: 1, minWidth: 250 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TrendingUpIcon sx={{ color: '#00BFFF' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Primary Opportunity</Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#00BFFF' }}>
                            {blueprint.opportunity}
                        </Typography>
                    </Paper>
                </Box>

                {/* ── SECTION 6: TRAINING PACE ── */}
                <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <SpeedIcon color="primary" />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Recommended Training Pace</Typography>
                    </Box>
                    <Typography variant="h3" sx={{ color: theme.palette.primary.main, fontWeight: 700, textAlign: 'center', my: 2 }}>
                        {blueprint.recommendedPace.min}–{blueprint.recommendedPace.max} WPM
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Start your training sessions at this pace with the ReaderFLEX guide dot. Adjust based on comprehension results.
                    </Typography>
                </Paper>

                {/* ── SECTION 7: GUIDED COMPARISON ── */}
                {guided && (
                    <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3, borderRadius: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Baseline vs Guided Reading</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">Baseline</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{b.wpm} WPM</Typography>
                                <Typography variant="body2" color="text.secondary">{b.comprehension}% comprehension</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">With Guide Dot</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#00BFFF' }}>{guided.wpm} WPM</Typography>
                                <Typography variant="body2" color="text.secondary">{guided.comprehension}% comprehension</Typography>
                            </Box>
                        </Box>
                        {guided.interpretation && (
                            <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 2, p: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                    {guided.interpretation.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                                    {guided.interpretation.body}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#00BFFF' }}>
                                    {guided.interpretation.recommendation}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                )}

                {/* ── SECTION 8: 7-DAY PLAN ── */}
                <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MenuBookIcon color="primary" />
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>Your 7-Day Training Plan</Typography>
                        </Box>
                        <Chip label={`${completedDays}/7 days`} color={completedDays === 7 ? 'success' : 'default'} />
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={(completedDays / 7) * 100}
                        sx={{ mb: 3, height: 8, borderRadius: 4 }}
                    />
                    {blueprint.sevenDayPlan.map((day, i) => (
                        <Box key={day.day}>
                            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                        <Checkbox
                                            checked={dayProgress[i] || false}
                                            onChange={() => handleToggleDay(day.day)}
                                            icon={<RadioButtonUncheckedIcon />}
                                            checkedIcon={<CheckCircleIcon sx={{ color: '#8DC63F' }} />}
                                            sx={{ p: 0.5 }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
                                                    Day {day.day}
                                                </Typography>
                                                <Chip label={day.estTime} size="small" variant="outlined" icon={<TimerIcon />} />
                                            </Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                {day.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                                                Focus: {day.focus}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                {day.exercise}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    ))}
                </Paper>

                {/* ── SECTION 9: WORKSHEETS ── */}
                <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3, borderRadius: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Recommended Worksheets</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Print these worksheets and use them alongside your 7-day plan. The PDF download includes all worksheets.
                    </Typography>
                    {blueprint.worksheets.map((ws, i) => (
                        <Box key={ws.id}>
                            {i > 0 && <Divider sx={{ my: 2 }} />}
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {i + 1}. {ws.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {ws.description}
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                {ws.sections.map((section, j) => (
                                    <li key={j}>
                                        <Typography component="span" variant="body2" color="text.secondary">
                                            {section.label}
                                        </Typography>
                                    </li>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Paper>

                {/* ── SECTION 10: NEXT MILESTONE ── */}
                <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <EmojiEventsIcon sx={{ color: '#FFD700' }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Your Next Milestone</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {blueprint.nextMilestone.badge}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {blueprint.nextMilestone.description}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={blueprint.nextMilestone.currentProgress}
                        sx={{ height: 10, borderRadius: 5 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {blueprint.nextMilestone.currentProgress}% progress toward this badge
                    </Typography>
                </Paper>

                {/* ── SECTION 11: CTAs ── */}
                <Paper sx={{
                    p: { xs: 3, md: 4 },
                    mb: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                    border: '1px solid',
                    borderColor: alpha('#667eea', 0.3),
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
                        Ready to Train?
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3, lineHeight: 1.7 }}>
                        Start your 3-day ReaderFLEX trial for $1. Full access to guided reading, comprehension tracking, and your full progress history. Cancel anytime.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<RocketLaunchIcon />}
                            component={Link}
                            to="/subscription?planType=membership&billingCycle=trial&from=blueprint"
                            sx={{
                                background: 'linear-gradient(135deg, #00BFFF 0%, #0099CC 100%)',
                                '&:hover': { background: 'linear-gradient(135deg, #00A5E0 0%, #0088BB 100%)' },
                            }}
                        >
                            Start $1 Trial
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<PersonAddIcon />}
                            component={Link}
                            to="/register?from=blueprint"
                        >
                            Create Free Account
                        </Button>
                    </Box>
                </Paper>

                {/* ── FOOTER ── */}
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                        ReaderFLEX — Speed Reading Platform
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        This blueprint was generated from your reading assessment results. It is not a clinical or diagnostic tool.
                    </Typography>
                </Box>
            </Container>
        </>
    );
}
